import { useState, useEffect } from 'react';
import ExcelJS from 'exceljs';
import './ProjectsSummary.css';
import CanvasJSReact from '@canvasjs/react-charts';
import ProjectsSummaryItem from '../ProjectsSummaryItem/ProjectsSummaryItem';
import { getProjects } from '../../utilities/api/projectsTimeline';
import { capitalizeFirstLetter } from '../../utilities/services/capitalizeFirstLetter';
import { preferenceOptions } from '../../utilities/services/preferencesOptions';
const CanvasJSChart = CanvasJSReact.CanvasJSChart;



function tableHeader() {
  return (
    <thead className='header'>
      <tr>
        <th className='projNum'>
            Project Number
        </th>
        <th className='projType'>
          Service
        </th>
        <th className='projTitle'>
          Title
        </th>
        <th className='projectStatus'>
          Status
        </th>
        <th className='clientNumber'>
          Client Number
        </th>
        <th className='clientName'>
          Client Name
        </th>
        <th className='submissionDate'>
          Submission
        </th>
        <th className='dueDate'>
          Analyst Deadline
        </th>
        <th className='clientDeadline'>
          Client Deadline
        </th>
        <th className='managerName'>
          Manager
        </th>
        <th className='assignedAnalystName'>
          Assigned Analyst
        </th>
        <th className='createdAt'>
          Creation
        </th>
        <th className='updatedAt'>
          Last Update
        </th>
      </tr>
    </thead>
  )
}

export default function ProjectsSummary({projectsData, services, user, handleDropdownClick}) {
  const [annualData, setAnnualData] = useState([])
  const [projectsByService, setProjectsByService] = useState([])
  const [projectsByServicePercent, setProjectsByServicePercent] = useState([])
  const [isExportingSheet, setIsExportingSheet] = useState(false)
  const [exportSheetColumns, setExportSheetColumns] = useState([])

  const projectsByServiceColumnChartOptions = {
    title: {
      // text: "Projects by Service"
    },
    toolTip: {
      shared: true,
      contentFormatter: function (e) {
        let content = ''
        e.entries.forEach(entry => {
          content += `<strong>${entry.dataPoint.fullName}</strong>: ${entry.dataPoint.y} ${entry.dataPoint.y === 1 ? 'Project' : 'Projects'}<br>`
        })
        return content
      }
    },
    theme: "light2",
    dataPointMaxWidth: 60,
    colorSet: "colorSet1",
    axisY: {
      title: "Quantity",
    },
    data: [{
      type: "column",
      // toolTipContent: "<b>{fullName}</b>: {y}",
      dataPoints: [
      ...projectsByService
      ]
    }]
  }

  const areaChartOptions = {
    animationEnabled: true,
    theme: "light2",
    title: {
        // text: "Projects Created Monthly" 
    },
    axisX: {
        valueFormatString: "MMM",
        intervalType: "month",
        interval: 1
    },
    axisY: {
        title: "Quantity",
        // suffix: "°F"
    },
    toolTip: {
      shared: true,
      contentFormatter: function (e) {
        let content = ''
        e.entries.forEach(entry => {
          content += `<strong>${entry.dataSeries.name}</strong>: ${entry.dataPoint.y} ${entry.dataPoint.y === 1 ? 'Project' : 'Projects'}<br>`
        })
        return content
      }
    },
    legend: {
        cursor: "pointer",
        itemclick: function (e) {
            if (typeof (e.dataSeries.visible) === "undefined" || e.dataSeries.visible) {
                e.dataSeries.visible = false;
            } else {
                e.dataSeries.visible = true;
            }
            e.chart.render();
        }
    },
    data: annualData
  };

  const projectsByServicePieChartOptions = {
    exportEnabled: true,
    animationEnabled: true,
    title: {
      // text: "projects By Service"
    },
    data: [{
      type: "pie",
      startAngle: 75,
      toolTipContent: "<b>{fullName}</b>: {y}%",
      // showInLegend: "true",
      // legendText: "{label}",
      indexLabelFontSize: 14,
      indexLabel: "{label} - {y}%",
      dataPoints: [
        ...projectsByServicePercent
      ]
    }]
  }

  const handleExportSheetColumnChange = (evt) => {
    const column = evt.target.value
    if (exportSheetColumns.includes(column)) {
      setExportSheetColumns(exportSheetColumns.filter(item => item !== column))
    } else {
      setExportSheetColumns([...exportSheetColumns, column])
    }
  }

  const createExcelSheet = async () => {
    setIsExportingSheet(true)
    try {
      const projects = await getProjects(user)
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Projects');
      sheet.columns = [
        exportSheetColumns.includes('projNum') && { header: 'PROJECT NUMBER', key: 'projectNumber', width: 20 },
        exportSheetColumns.includes('projType') && { header: 'SERVICE', key: 'service', width: 20 },
        exportSheetColumns.includes('projTitle') && { header: 'TITLE', key: 'title', width: 20 },
        exportSheetColumns.includes('projectStatus') && { header: 'PROJECT STATUS', key: 'projectStatus', width: 20 },
        exportSheetColumns.includes('clientNumber') && { header: 'CLIENT NUMBER', key: 'clientNumber', width: 20 },
        exportSheetColumns.includes('clientName') && { header: 'CLIENT NAME', key: 'clientName', width: 20 },
        exportSheetColumns.includes('submissionDate') && { header: 'SUBMISSION DATE', key: 'submissionDate', width: 20 },
        exportSheetColumns.includes('dueDate') && { header: 'ANALYST DEADLINE', key: 'analystDeadline', width: 20 },
        exportSheetColumns.includes('clientDeadline') && { header: 'CLIENT DEADLINE', key: 'clientDeadline', width: 20 },
        exportSheetColumns.includes('managerName') && { header: 'MANAGER', key: 'manager', width: 20 },
        exportSheetColumns.includes('assignedAnalystName') && { header: 'ANALYST', key: 'analyst', width: 20 },
        exportSheetColumns.includes('ndaStatus') && { header: 'NDA STATUS', key: 'ndaStatus', width: 20 },
        exportSheetColumns.includes('ndaURL') && { header: 'NDA LINK', key: 'ndaURL', width: 30 },
        exportSheetColumns.includes('contractStatus') && { header: 'CONTRACT STATUS', key: 'contractStatus', width: 20 },
        exportSheetColumns.includes('contractURL') && { header: 'CONTRACT LINK', key: 'contractURL', width: 30 },
        exportSheetColumns.includes('firstInvoiceStatus') && { header: 'FIRST INVOICE STATUS', key: 'firstInvoiceStatus', width: 20 },
        exportSheetColumns.includes('firstInvoiceURL') && { header: 'FIRST INVOICE LINK', key: 'firstInvoiceURL', width: 30 },
        exportSheetColumns.includes('intakeFormStatus') && { header: 'FORM STATUS', key: 'intakeFormStatus', width: 20 },
        exportSheetColumns.includes('formURL') && { header: 'FORM LINK', key: 'formURL', width: 30 },
        exportSheetColumns.includes('projectReportStatus') && { header: 'PROJECT REPORT STATUS', key: 'projectReportStatus', width: 20 },
        exportSheetColumns.includes('projectReportURL') && { header: 'PROJECT REPORT LINK', key: 'projectReportURL', width: 30 },
        exportSheetColumns.includes('secondInvoiceStatus') && { header: 'SECOND INVOICE STATUS', key: 'secondInvoiceStatus', width: 20 },
        exportSheetColumns.includes('secondInvoiceURL') && { header: 'SECOND INVOICE LINK', key: 'secondInvoiceURL', width: 30 },
        exportSheetColumns.includes('otherURLs') && { header: 'OTHER LINK 1', key: 'otherURL1', width: 30 },
        exportSheetColumns.includes('otherURLs') && { header: 'OTHER LINK 2', key: 'otherURL2', width: 30 },
        exportSheetColumns.includes('otherURLs') && { header: 'OTHER LINK 3', key: 'otherURL3', width: 30 },
        exportSheetColumns.includes('otherURLs') && { header: 'OTHER LINK 4', key: 'otherURL4', width: 30 },
        exportSheetColumns.includes('createdAt') && { header: 'CREATION', key: 'createdAt', width: 20 },
        exportSheetColumns.includes('updatedAt') && { header: 'LAST UPDATE', key: 'updatedAt', width: 20 }
      ];
      sheet.getRow(1).font = { bold: true, size: 14 };
      projects.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).forEach(project => {
        sheet.addRow({
          projectNumber: project.projNum,
          service: project.projType,
          title: project.projTitle? capitalizeFirstLetter(project.projTitle) : '---',
          projectStatus: project.projectStatus,
          clientNumber: project.clientNumber? parseInt(project.clientNumber) : '---',
          clientName: project.clientName? capitalizeFirstLetter(project.clientName) : '---',
          submissionDate: project.submissionDate? new Date(project.submissionDate).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '---',
          analystDeadline: project.dueDate? new Date(project.dueDate).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '---',
          clientDeadline: project.clientDeadline? new Date(project.clientDeadline).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '---',
          manager: project.managerName? capitalizeFirstLetter(project.managerName) : '---',
          analyst: project.assignedAnalystName? capitalizeFirstLetter(project.assignedAnalystName) : '---',
          ndaStatus: project.ndaStatus? project.ndaStatus : '---',
          ndaURL: project.ndaURL? project.ndaURL : '---',
          contractStatus: project.contractStatus? project.contractStatus : '---',
          contractURL: project.contractURL? project.contractURL : '---',
          firstInvoiceStatus: project.firstInvoiceStatus? project.firstInvoiceStatus : '---',
          firstInvoiceURL: project.firstInvoiceURL? project.firstInvoiceURL : '---',
          intakeFormStatus: project.intakeFormStatus? project.intakeFormStatus : '---',
          formURL: project.formURL? project.formURL : '---',
          projectReportStatus: project.projectReportStatus? project.projectReportStatus : '---',
          projectReportURL: project.projectReportURL? project.projectReportURL : '---',
          secondInvoiceStatus: project.secondInvoiceStatus? project.secondInvoiceStatus : '---',
          secondInvoiceURL: project.secondInvoiceURL? project.secondInvoiceURL : '---',
          otherURL1: project.otherURL1? project.otherURL1 : '---',
          otherURL2: project.otherURL2? project.otherURL2 : '---',
          otherURL3: project.otherURL3? project.otherURL3 : '---',
          otherURL4: project.otherURL4? project.otherURL4 : '---',
          createdAt: new Date(project.createdAt).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }),
          updatedAt: new Date(project.updatedAt).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
        })
      })
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Projects.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error)
    }
    setIsExportingSheet(false)
  }

  useEffect(() => {
    const currentDate = new Date();
    const tempAnnualData = projectsData.annualData.map(data => {
      return {
          type: "area",
          name: (data.year).toString(),
          showInLegend: true,
          xValueFormatString: "MMMM",
          yValueFormatString: "#,##0",
          // Use default year of 2024, so that line charts are aligned
          dataPoints: [
            { date: new Date(data.year, 0), x: new Date(2024, 0), y: data.monthlyData.JAN },
            { date: new Date(data.year, 1), x: new Date(2024, 1), y: data.monthlyData.FEB },
            { date: new Date(data.year, 2), x: new Date(2024, 2), y: data.monthlyData.MAR },
            { date: new Date(data.year, 3), x: new Date(2024, 3), y: data.monthlyData.APR },
            { date: new Date(data.year, 4), x: new Date(2024, 4), y: data.monthlyData.MAY },
            { date: new Date(data.year, 5), x: new Date(2024, 5), y: data.monthlyData.JUN },
            { date: new Date(data.year, 6), x: new Date(2024, 6), y: data.monthlyData.JUL },
            { date: new Date(data.year, 7), x: new Date(2024, 7), y: data.monthlyData.AUG },
            { date: new Date(data.year, 8), x: new Date(2024, 8), y: data.monthlyData.SEP },
            { date: new Date(data.year, 9), x: new Date(2024, 9), y: data.monthlyData.OCT },
            { date: new Date(data.year, 10), x: new Date(2024, 10), y: data.monthlyData.NOV },
            { date: new Date(data.year, 11), x: new Date(2024, 11), y: data.monthlyData.DEC }
          ].filter(dataPoint => currentDate >= dataPoint.date).map(dataPoint => {
            return {x: dataPoint.x, y: dataPoint.y}
          })
        }
    })
    setAnnualData(tempAnnualData)

    let totalProjects = 0

    Object.keys(projectsData.projectsByService).forEach(service => {
      totalProjects += projectsData.projectsByService[service]
    })
  
    const tempProjectsByService = Object.keys(projectsData.projectsByService).map(service => {
      const serviceObject = services.find(s => s.code === service)
      return {label: service, y: projectsData.projectsByService[service], fullName: serviceObject ? serviceObject.name : service}
    })
    setProjectsByService(tempProjectsByService)

    const tempProjectsByServicePercent = tempProjectsByService.map(service => {
      const serviceObject = services.find(s => s.code === service.label)
      return {label: service.label, y: Math.round((service.y / totalProjects) * 100), fullName: serviceObject ? serviceObject.name : service.label}
    })
    setProjectsByServicePercent(tempProjectsByServicePercent)
    // eslint-disable-next-line
  }, [projectsData])

  return (
    <div className='ProjectsSummary'>
      <div className='exportProjectsContainer dropdown'>
        <button className='exportProjects' title='Export Projects to Spreadsheet' id='exportProjectsDropDownToggle' data-title='Export Projects' data-bs-toggle="dropdown" data-bs-auto-close='outside' aria-expanded="false">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="20px" fill='#FFFFFF'>
            <path d="M 28.90625 1.96875 C 28.863281 1.976563 28.820313 1.988281 28.78125 2 L 11.5 2 C 9.585938 2 8 3.558594 8 5.46875 L 8 43.90625 C 8 46.160156 9.867188 48 12.125 48 L 37.875 48 C 40.132813 48 42 46.160156 42 43.90625 L 42 15.1875 C 42.027344 15.054688 42.027344 14.914063 42 14.78125 L 42 14.5 C 42.007813 14.234375 41.90625 13.972656 41.71875 13.78125 L 30.21875 2.28125 C 30.027344 2.09375 29.765625 1.992188 29.5 2 L 29.1875 2 C 29.097656 1.976563 29 1.964844 28.90625 1.96875 Z M 11.5 4 L 28 4 L 28 12.34375 C 28 14.355469 29.644531 16 31.65625 16 L 40 16 L 40 43.90625 C 40 45.074219 39.054688 46 37.875 46 L 12.125 46 C 10.945313 46 10 45.074219 10 43.90625 L 10 5.46875 C 10 4.644531 10.660156 4 11.5 4 Z M 30 4.9375 L 39.0625 14 L 31.65625 14 C 30.722656 14 30 13.277344 30 12.34375 Z M 17 24 L 17 38 L 33 38 L 33 24 Z M 19 26 L 24 26 L 24 28 L 19 28 Z M 26 26 L 31 26 L 31 28 L 26 28 Z M 19 30 L 24 30 L 24 32 L 19 32 Z M 26 30 L 31 30 L 31 32 L 26 32 Z M 19 34 L 24 34 L 24 36 L 19 36 Z M 26 34 L 31 34 L 31 36 L 26 36 Z"/>
          </svg>
          <span>
            &nbsp;&nbsp;Export Projects
          </span>
        </button>
        <div className='dropdown-menu exportProjectsDropDown' aria-labelledby="exportProjectsDropDownToggle" onClick={handleDropdownClick}>
          <div className='description'>Select Fields to Export</div>
          <ul>
          { Object.keys(preferenceOptions['projects']).map((option, index) => (
            <li key={index} className='dropdown-item'>
              <div className='form-check'>
                  <input className='form-check-input' 
                    id={`projects-${option}-${index}`} 
                    type="checkbox" 
                    defaultChecked={false} 
                    value={option}
                    disabled={isExportingSheet}
                    onChange={handleExportSheetColumnChange} 
                  />
                  <label className='form-check-label' htmlFor={`projects-${option}-${index}`}>
                      {preferenceOptions['projects'][option]}
                  </label>
              </div>
            </li>
            ))}
          </ul>
          <div className='buttonContainer'>
            <button className='exportProjects' onClick={createExcelSheet} disabled={isExportingSheet || exportSheetColumns.length===0}>
              Export
            </button>
          </div>
        </div>
        
      </div>
      <div className='annualProjectsInfo'>
        <div className='title1'>Monthly Data</div>
        <CanvasJSChart options = {areaChartOptions} />
      </div>
      <div className='group'>
        <div className='criticalProjectCount'>
          <div className='overdueProjectCount container'>
            <div className='title'>Overdue Projects</div>
            <div className='num'>{projectsData.overdueProjectsCount}</div>
          </div>
          <div className='imminentProjectCount container'>
            <div className='title'>Imminent Projects</div>
            <div className='num'>{projectsData.upcomingDeadlineProjectsCount}</div>
          </div>
        </div>
        <div className='criticalProjects'>
          <div className='title1'>
            <svg xmlns="http://www.w3.org/2000/svg" width="22" fill="#E21D12" className="bi bi-exclamation-triangle-fill" viewBox="0 0 16 16">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
            </svg>
            <span>&nbsp;&nbsp;Critical Projects</span>
          </div>
          <div className='nav nav-tabs flex-row navigation' id="v-tab1" role='tablist' aria-orientation="horizontal">
              <button className="nav-link active" id='overdueProjectsTab' data-bs-toggle="tab" role="tab" aria-controls="overdueProjects" aria-selected="true" data-bs-target="#overdueProjects" type="button">
                  Overdue
              </button>
              <button className="nav-link" id='imminentProjectTab' data-bs-toggle="tab" role="tab" aria-controls="imminentProject" aria-selected="false" data-bs-target="#imminentProject" type="button">
                  Imminent
              </button>
          </div>
          <div className='content tab-content'>
            <div className="overdueProjects tab-pane fade show active" id="overdueProjects" role="tabpanel" aria-labelledby="overdueProjectsTab">
              { projectsData.overdueProjectsData.length === 0 ?
              <div className='noProjects'>No Overdue Projects</div>
              :
              <table>
                {tableHeader()}
                <tbody>
                { projectsData.overdueProjectsData.map((project, idx) => (
                  <ProjectsSummaryItem key={idx} projectItem={project} backgroundStyle={'overdue'} user={user} />
                ))}
                </tbody>
              </table>
              }
            </div>
            <div className="imminentProject tab-pane fade" id="imminentProject" role="tabpanel" aria-labelledby="imminentProjectTab">
              { projectsData.upcomingDeadlineProjectsData.length === 0 ?
              <div className='noProjects'>No Imminent Projects</div>
              :
              <table>
                {tableHeader()}
                <tbody>
                { projectsData.upcomingDeadlineProjectsData.map((project, idx) => (
                  <ProjectsSummaryItem key={idx} projectItem={project} backgroundStyle={'imminent'} user={user} />
                ))}
                </tbody>
              </table>
              }
            </div>
          </div>
        </div>
      </div>
      <div className='group'>
        <div className='Projects'>
          <div className='title1'>Most Recent Projects</div>
          <div className='nav nav-tabs flex-row navigation' id="v-tab2" role='tablist' aria-orientation="horizontal">
              { ['admin', 'manager'].includes(user.userType) &&
              <button className="nav-link" id='unassignedProjectsTab' data-bs-toggle="tab" role="tab" aria-controls="unassignedProjects" aria-selected="true" data-bs-target="#unassignedProjects" type="button">
                  Unassigned
              </button>
              }
              <button className="nav-link active" id='inProgressProjectTab' data-bs-toggle="tab" role="tab" aria-controls="inProgressProject" aria-selected="false" data-bs-target="#inProgressProject" type="button">
                  Ongoing
              </button>
              <button className="nav-link" id='completeProjectTab' data-bs-toggle="tab" role="tab" aria-controls="completeProject" aria-selected="false" data-bs-target="#completeProject" type="button">
                  Complete
              </button>
          </div>
          <div className='content tab-content'>
            { ['admin', 'manager'].includes(user.userType) &&
            <div className="unassignedProjects tab-pane fade" id="unassignedProjects" role="tabpanel" aria-labelledby="unassignedProjectsTab">
              { projectsData.unassignedProjectsData.length === 0 ?
              <div className='noProjects'>No Unassigned Projects</div>
              :
              <table>
                {tableHeader()}
                <tbody>
                { projectsData.unassignedProjectsData.map((project, idx) => (
                  <ProjectsSummaryItem key={idx} projectItem={project} backgroundStyle={'unassigned'} user={user} />
                ))}
                </tbody>
              </table>
              }
            </div>
            }
            <div className="inProgressProject tab-pane fade show active" id="inProgressProject" role="tabpanel" aria-labelledby="inProgressProjectTab">
              { projectsData.inProgressProjectsData.length === 0 ?
              <div className='noProjects'>No Ongoing Projects</div>
              :
              <table>
                {tableHeader()}
                <tbody>
                { projectsData.inProgressProjectsData.map((project, idx) => (
                  <ProjectsSummaryItem key={idx} projectItem={project} backgroundStyle={'inProgress'} user={user} />
                ))}
                </tbody>
              </table>
              }
            </div>
            <div className="completeProject tab-pane fade" id="completeProject" role="tabpanel" aria-labelledby="completeProjectTab">
              { projectsData.completedProjectsData.length === 0 ?
              <div className='noProjects'>No Complete Projects</div>
              :
              <table>
                {tableHeader()}
                <tbody>
                { projectsData.completedProjectsData.map((project, idx) => (
                  <ProjectsSummaryItem key={idx} projectItem={project} backgroundStyle={'complete'} user={user} />
                ))}
                </tbody>
              </table>
              }
            </div>
          </div>
        </div>
        <div className='ProjectCount'>
          { ['admin', 'manager'].includes(user.userType) &&
          <div className='UnassignedProjectCount container'>
            <div className='title'>Unassigned Projects</div>
            <div className='num'>{projectsData.unassignedProjectsCount}</div>
          </div>
          }
          <div className='inProgressProjectCount container'>
            <div className='title'>Ongoing Projects</div>
            <div className='num'>{projectsData.inProgressProjectsCount}</div>
          </div>
        </div>
      </div>
      <div className='group'>
        <div className='projectsByService'>
          <div className='title1'>Service Distribution</div>
          <CanvasJSChart options = {projectsByServiceColumnChartOptions}/>
        </div>
        <div className='projectsByServicePercent'>
          <div className='title1'>Service Distribution (%)</div>
          <CanvasJSChart options = {projectsByServicePieChartOptions}/>
        </div>
      </div>
    </div>
  )
}
